/**
 * Empty states (M3.7): what the screen says when a section, or the whole
 * ledger, has nothing in it.
 *
 * An empty ledger can be correct (Constitution invariant 10, "trustworthy
 * silence is a product outcome"; Pitch: no card minimum). So an empty section
 * is not an unfinished screen and must not read like one — and it must not
 * read like an all-clear either. The v1 Spec's non-goal is explicit: "No
 * promise that an empty ledger means the user has no obligations outside
 * observed evidence." Every line here therefore does three things and only
 * three:
 *
 * 1. **Reassures** — the sentence the section already had, unchanged.
 * 2. **Shows its evidence** — what was examined and what became of it, in
 *    counts the ledger can back. Silence that cannot be inspected is not
 *    trustworthy, it is just silence.
 * 3. **Keeps its boundary** — the limit is verification and the mail read,
 *    never the user's life. "You're all caught up" would be a lie; so would
 *    any sentence that implies nothing is owed.
 *
 * The strings are recorded in `docs/copy.md` and are Levi's to reword; the
 * tests here say what each line must and must not claim, so rewording is safe.
 */
import type { Claim, Ledger, LedgerItem, LedgerSectionId, PersonId, Surface } from '@cordelia/core'

/** What became of the candidates that pointed at one section. */
export type SectionFacts = {
  /** Candidates whose own direction pointed at this section. */
  readonly considered: number
  /** Of those, the ones verification established. */
  readonly verified: number
  /** Where the established ones are instead, in words, most numerous first. */
  readonly elsewhere: readonly { readonly place: string; readonly count: number }[]
}

export type EmptyFacts = {
  readonly messagesRead: number
  readonly windowDays: number
  readonly cutoff: string
  readonly candidates: number
  readonly verified: number
  readonly disputed: number
  readonly rejected: number
  readonly suppressed: number
  /** Suppressions grouped by reason, in words, most numerous first. */
  readonly suppressions: readonly { readonly reason: string; readonly count: number }[]
  /** Items that resolved in the mail itself and never needed a question. */
  readonly resolved: number
  /** Past commitments history looked at, whether or not any needed asking about. */
  readonly historyChecked: number
  readonly sections: Readonly<Record<LedgerSectionId, SectionFacts>>
}

/** The reassurance, unchanged from the sentence each section already had. */
export const REASSURANCE: Readonly<Record<LedgerSectionId, string>> = {
  'you-owe': 'Nothing you owe survived verification.',
  'youre-waiting-on': 'No open commitments to you survived verification.',
  'may-be-expected': 'No credible expectations of you survived verification.',
  'probably-done': 'No high-quality past commitments needed a check.',
}

/**
 * What a policy suppression means, in words a person can read. The keys are
 * the closed set the pipeline produces: `prefilter/` before any claim exists,
 * `route/` after openness, and `guards/` for an invariant that did not hold.
 */
const POLICY_REASON: Readonly<Record<string, string>> = {
  'prefilter:bulk': 'a bulk or automated message',
  'prefilter:calendar': 'a calendar notice',
  'prefilter:empty': 'a message with nothing written in it',
  'prefilter:no_candidate_language': 'a message with no language of commitment in it',
  low_quality: 'too thin to stand on',
  not_about_user: 'not about you',
  outbound_request_unaccepted: 'a request nobody accepted',
  stale_expectation: 'an expectation with nothing to show it is still open',
  unestablished_stale: 'never established and long past',
}

const USER_REASON: Readonly<Record<string, string>> = {
  'user:dismiss': 'dismissed by you',
  'user:irrelevant_or_private': 'marked irrelevant or private by you',
  'user:not_a_commitment': 'marked not a commitment by you',
  'user:draft_abandoned': 'marked a draft abandoned on purpose by you',
}

export function suppressionReason(reason: string): string {
  if (reason.startsWith('invariant_violation:')) return 'held back by an invariant check'
  return USER_REASON[reason] ?? POLICY_REASON[reason] ?? reason.replace(/_/g, ' ')
}

const plural = (n: number, one: string, many = `${one}s`): string => (n === 1 ? one : many)

/** A number as it is written: 1,240, never 1240. The locale is fixed, so tests are. */
const count = (n: number): string => n.toLocaleString('en-US')

/** English for a short list: "a, b and c". */
function list(parts: readonly string[]): string {
  if (parts.length <= 1) return parts[0] ?? ''
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] ?? ''}`
}

/**
 * Which section a *candidate* pointed at, read from the direction the claim
 * already carries. This is not routing — routing is
 * `packages/pipeline/src/route/` and runs after verification, lineage and
 * openness. Nothing here changes a claim; it only counts what was considered,
 * so an empty section can say what it looked at.
 */
function claimSection(claim: Claim, user: PersonId): LedgerSectionId | null {
  if (claim.owed_by === user) return 'you-owe'
  if (claim.owed_to === user) return 'youre-waiting-on'
  if (claim.owed_by === 'unresolved') return 'may-be-expected'
  return null
}

/** Where an established claim ended up, in words. */
function placeOf(item: LedgerItem): string {
  switch (item.surface) {
    case 'you-owe':
      return 'in You owe'
    case 'waiting-on':
      return "in You're waiting on"
    case 'may-be-expected':
      return 'in May be expected'
    case 'history':
      return item.history_rank === null ? 'resolved in the mail itself' : 'in Probably done?'
    case 'suppressed':
      return 'suppressed with a reason'
  }
}

const byCount = <T extends { count: number }>(a: T, b: T): number => b.count - a.count

function tally(values: readonly string[]): readonly { place: string; count: number }[] {
  const counts = new Map<string, number>()
  for (const v of values) counts.set(v, (counts.get(v) ?? 0) + 1)
  return [...counts].map(([place, count]) => ({ place, count })).sort(byCount)
}

export function emptyFacts(input: {
  readonly ledger: Ledger
  readonly cutoff: string
  readonly windowDays: number
  readonly messagesRead: number
}): EmptyFacts {
  const { ledger } = input
  const itemByClaim = new Map(ledger.items.map((i) => [i.claim_id, i]))
  const state = (s: Claim['verification_state']): number =>
    ledger.claims.filter((c) => c.verification_state === s).length

  const sections = {} as Record<LedgerSectionId, SectionFacts>
  for (const id of ['you-owe', 'youre-waiting-on', 'may-be-expected', 'probably-done'] as const) {
    const pointed = ledger.claims.filter((c) => claimSection(c, ledger.user) === id)
    const established = pointed.filter((c) => c.verification_state === 'verified')
    sections[id] = {
      considered: pointed.length,
      verified: established.length,
      elsewhere: tally(
        established
          .map((c) => itemByClaim.get(c.id))
          .filter((i): i is LedgerItem => i !== undefined)
          .map(placeOf),
      ),
    }
  }

  const reasons = new Map<string, number>()
  for (const s of ledger.suppressions) {
    const words = suppressionReason(s.reason)
    reasons.set(words, (reasons.get(words) ?? 0) + 1)
  }

  const onHistory = ledger.items.filter((i) => i.surface === 'history')
  return {
    messagesRead: input.messagesRead,
    windowDays: input.windowDays,
    cutoff: input.cutoff,
    candidates: ledger.claims.length,
    verified: state('verified'),
    disputed: state('disputed'),
    rejected: state('rejected'),
    suppressed: ledger.suppressions.length,
    suppressions: [...reasons].map(([reason, count]) => ({ reason, count })).sort(byCount),
    resolved: onHistory.filter((i) => i.history_rank === null).length,
    historyChecked: onHistory.length,
    sections,
  }
}

/**
 * Probably done? has to say that history was looked at, or its silence reads
 * as history not having been considered at all.
 */
function historyEvidence(facts: EmptyFacts): string {
  if (facts.resolved > 0)
    return `History was checked: ${count(facts.resolved)} past ${plural(facts.resolved, 'commitment')} resolved in the mail itself, so ${facts.resolved === 1 ? 'it does' : 'they do'} not need your memory.`
  return `History was checked as far back as ${facts.windowDays} days. Nothing in it was left hanging without a message either way.`
}

/**
 * When nothing even pointed at a section, say so in that section's own terms.
 * These describe what extraction proposed, not what the messages contain: the
 * limit of the reading is what the reassurance sentence before them names.
 */
const NOTHING_PROPOSED: Readonly<Record<Exclude<LedgerSectionId, 'probably-done'>, string>> = {
  'you-owe': 'Nothing you owe was proposed from the',
  'youre-waiting-on': 'Nothing owed to you was proposed from the',
  'may-be-expected': 'Nothing was left open about who owes it, across the',
}

function sectionEvidence(id: LedgerSectionId, facts: EmptyFacts): string {
  if (id === 'probably-done') return historyEvidence(facts)
  const s = facts.sections[id]
  const read = `${count(facts.messagesRead)} ${plural(facts.messagesRead, 'message')} read.`
  if (s.considered === 0) return `${NOTHING_PROPOSED[id]} ${read}`
  const considered = `${count(s.considered)} ${plural(s.considered, 'candidate')} pointed this way`
  if (s.verified === 0) return `${considered}, and none survived verification.`
  const where = list(s.elsewhere.map((e) => `${count(e.count)} ${e.place}`))
  return `${considered}; ${count(s.verified)} verified, and ${where}.`
}

/** The one line an empty section shows: reassurance, then its evidence. */
export function sectionEmptyLine(id: LedgerSectionId, facts: EmptyFacts): string {
  return `${REASSURANCE[id]} ${sectionEvidence(id, facts)}`
}

/**
 * The whole-ledger empty state: Trace-like detail, so silence is legible
 * (invariant 10). What was found, what was verified, what was suppressed and
 * why, what resolved on its own — then the boundary, which is the part that
 * has to be true rather than kind.
 */
export function ledgerEmptyLines(facts: EmptyFacts): readonly string[] {
  const lines: string[] = ['Nothing survived verification.']

  const outcomes = [
    facts.verified > 0 ? `${count(facts.verified)} verified` : null,
    facts.disputed > 0 ? `${count(facts.disputed)} disputed` : null,
    facts.rejected > 0 ? `${count(facts.rejected)} rejected` : null,
  ].filter((p): p is string => p !== null)
  lines.push(
    facts.candidates === 0
      ? `Nothing in the ${count(facts.messagesRead)} ${plural(facts.messagesRead, 'message')} read looked like a commitment, so there was nothing to verify.`
      : `${count(facts.candidates)} ${plural(facts.candidates, 'candidate')} ${plural(facts.candidates, 'was', 'were')} considered${outcomes.length > 0 ? `: ${list(outcomes)}` : ', and none was established'}.`,
  )

  if (facts.suppressed > 0) {
    const only = facts.suppressions.length === 1 ? facts.suppressions[0] : undefined
    const why =
      only === undefined
        ? list(facts.suppressions.map((s) => `${count(s.count)} ${s.reason}`))
        : only.reason
    lines.push(
      `${count(facts.suppressed)} suppressed with a reason: ${why}. Each is reversible and stays in Trace.`,
    )
  }
  if (facts.resolved > 0)
    lines.push(
      `${count(facts.resolved)} already resolved in the mail itself, so ${facts.resolved === 1 ? 'it needs' : 'they need'} nothing from you.`,
    )

  lines.push(
    `That does not mean you have no obligations. It means none is established by the mail read: anything agreed in a call, a meeting or another app is not here, and neither is anything outside the last ${facts.windowDays} days.`,
  )
  return lines
}

/** Every surface a card can be on, for the tests that walk `placeOf`. */
export const PLACES: readonly Surface[] = [
  'you-owe',
  'waiting-on',
  'may-be-expected',
  'history',
  'suppressed',
]
